import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import uniqBy from "lodash-es/uniqBy";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import User from "@mapmycustomers/shared/types/User";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import { SearchField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import ToggleEntityCheckbox from "@app/component/input/ToggleEntityCheckbox";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";
import toggleItemInSet, { toggleMultiplyItemsInSet } from "@app/util/toggleItemInSet";
import { getRoleDisplayName } from "@app/util/ui";

import styles from "./SelectUserTree.module.scss";

const noUsersMessage = defineMessage({
  id: "selectUsersTree.users.noEntries",
  defaultMessage: "No Users Found",
  description: "'No entries' message for the select users dialog",
});

interface TeamWithMembers {
  id?: number;
  isOwners: boolean;
  members: User[];
  name?: string;
}

interface Props {
  allUsers: User[];
  isDisabled?: (user: User) => boolean;
  onChange?: (userIds: User["id"][]) => void;
  tooltipGetter?: (user: User) => ReactNode;
  value: User["id"][];
}

// TODO: extract as a separate component, otherwise it duplicates same component in GroupMode's Sidebar
const SelectUsersTree: React.FC<Props> = ({
  allUsers,
  isDisabled,
  onChange,
  tooltipGetter,
  value,
}) => {
  const intl = useIntl();
  const [query, setQuery] = useState<string>("");
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? allUsers.filter((user) => userDisplayName(user).toLowerCase().includes(q))
      : allUsers;
  }, [allUsers, query]);

  const selectedUsers = new Set(value);

  const handleSelectAllCheckboxChange = useCallback(
    (checked) => {
      const userIds = toggleMultiplyItemsInSet(
        new Set(value),
        filteredUsers.map(({ id }) => id),
        checked
      );
      onChange?.(Array.from(userIds));
    },
    [filteredUsers, onChange, value]
  );

  const groupedUsers: TeamWithMembers[] = useMemo(() => {
    const teams = uniqBy(
      filteredUsers.flatMap((user) => user.teams),
      "id"
    ).sort(nameComparator);

    return [
      {
        isOwners: true,
        members: filteredUsers.filter(({ role }) => role.key === RoleEnum.OWNER),
      },
      ...teams.map(({ id, name }) => ({
        id,
        isOwners: false,
        members: filteredUsers.filter(
          ({ role, teams }) => role.key !== RoleEnum.OWNER && teams.some((team1) => team1.id === id)
        ),
        name,
      })),
    ];
  }, [filteredUsers]);

  const allSelected = filteredUsers.every((user) => selectedUsers.has(user.id));
  const isFiltering = filteredUsers.length < allUsers.length;
  const selectAllLabel =
    (allSelected
      ? intl.formatMessage({
          id: "selectUsersTree.users.deselectAll",
          defaultMessage: "Deselect All",
          description: "Deselect all message for the select users tree",
        })
      : intl.formatMessage({
          id: "selectUsersTree.users.selectAll",
          defaultMessage: "Select All",
          description: "Select all message for the select users tree",
        })) +
    (isFiltering
      ? intl.formatMessage({
          id: "selectUsersTree.users.filterTitle",
          defaultMessage: " Users",
          description: "Title message for the select users tree",
        })
      : "");

  const getCheckedMembersCount = useCallback(
    (users: User[]) => {
      const selectedUsers = new Set(value);
      return users.filter((user) => selectedUsers.has(user.id)).length;
    },
    [value]
  );

  const handleSelectTeam = useCallback(
    (users: User[], select: boolean) => {
      onChange?.(
        Array.from(
          toggleMultiplyItemsInSet(
            new Set(value),
            users.map(({ id }) => id),
            select
          )
        )
      );
    },
    [onChange, value]
  );

  const handleToggleUser = useCallback(
    (user: User, checked: boolean) => {
      onChange?.(Array.from(toggleItemInSet(new Set(value), user.id, checked)));
    },
    [onChange, value]
  );

  return (
    <>
      <Checkbox
        checked={allSelected}
        indeterminate={
          allSelected ? false : filteredUsers.some((user) => selectedUsers.has(user.id))
        }
        onChange={handleSelectAllCheckboxChange}
      >
        {selectAllLabel}
      </Checkbox>
      <SearchField
        className={styles.searchInput}
        onChange={setQuery}
        placeholder={intl.formatMessage({
          id: "selectUsersTree.default.search.placeholder",
          defaultMessage: "Search",
          description: "Placeholder for search select user dialog tree",
        })}
        value={query}
      />
      <div className={styles.container}>
        {groupedUsers
          .filter(({ isOwners }) => isOwners)
          .map((team) =>
            team.members.map((owner) => (
              <ToggleEntityCheckbox
                checked={selectedUsers.has(owner.id)}
                disabled={isDisabled?.(owner)}
                displayName={intl.formatMessage(
                  {
                    id: "selectUsersTree.default.search.userCheckbox",
                    defaultMessage: "{name}{isRep, select, true {} other { ({roleName})}}",
                    description: "Checkbox for search in select user tree",
                  },
                  {
                    isRep: [RoleEnum.MEMBER, RoleEnum.READ_ONLY].includes(owner.role.key),
                    name: userDisplayName(owner),
                    roleName: getRoleDisplayName(intl, owner.role.key),
                  }
                )}
                entity={owner}
                key={owner.id}
                onToggle={handleToggleUser}
                tooltip={tooltipGetter?.(owner) ?? userDisplayName(owner)}
              />
            ))
          )}
        {groupedUsers
          .filter(({ isOwners }) => !isOwners)
          .map((team) => {
            const selectedCount = getCheckedMembersCount(team.members);
            const allSelected = selectedCount === team.members.length;
            const someSelected = !allSelected && selectedCount > 0;
            return (
              <React.Fragment key={team.id}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => handleSelectTeam(team.members, !allSelected)}
                >
                  {team.name}
                </Checkbox>
                {team.members.map((member) => (
                  <ToggleEntityCheckbox
                    checked={selectedUsers.has(member.id)}
                    className={styles.nestedItem}
                    disabled={isDisabled?.(member)}
                    displayName={`${userDisplayName(member)} ${
                      member.role.key !== RoleEnum.MEMBER
                        ? `(${getRoleDisplayName(intl, member.role.key)})`
                        : ""
                    }`}
                    entity={member}
                    key={member.id}
                    onToggle={handleToggleUser}
                    tooltip={tooltipGetter?.(member) ?? userDisplayName(member)}
                  />
                ))}
              </React.Fragment>
            );
          })}

        {!filteredUsers.length && (
          <div className={styles.noEntitiesMessage}>
            <FormattedMessage {...noUsersMessage} />
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsers(state),
});

export default connect(mapStateToProps)(SelectUsersTree);
